import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

function drawPixelBox(canvas, pixelBox, naturalSize) {
  if (!canvas || !pixelBox || !naturalSize.width || !naturalSize.height) return;
  const context = canvas.getContext("2d");
  if (!context) return;

  canvas.width = naturalSize.width;
  canvas.height = naturalSize.height;
  context.clearRect(0, 0, naturalSize.width, naturalSize.height);

  const lineWidth = Math.max(3, Math.round(Math.min(naturalSize.width, naturalSize.height) * 0.005));
  context.strokeStyle = "#f44336";
  context.lineWidth = lineWidth;
  context.strokeRect(pixelBox.x, pixelBox.y, pixelBox.width, pixelBox.height);
}

function MarketFlyerImageViewer({
  src,
  alt,
  pixelBox,
  maxHeight = 480,
  viewerBackground = "#000",
}) {
  const [rotation, setRotation] = useState(0);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const overlayCanvasRef = useRef(null);

  const hasOverlay = useMemo(() => {
    return Boolean(
      pixelBox &&
      Number.isFinite(pixelBox.x) &&
      Number.isFinite(pixelBox.y) &&
      Number.isFinite(pixelBox.width) &&
      Number.isFinite(pixelBox.height)
    );
  }, [pixelBox]);

  useEffect(() => {
    if (!hasOverlay) return;
    drawPixelBox(overlayCanvasRef.current, pixelBox, naturalSize);
  }, [hasOverlay, pixelBox, naturalSize]);

  return (
    <TransformWrapper
      minScale={0.5}
      maxScale={6}
      limitToBounds={false}
      wheel={{ step: 0.2 }}
      pinch={{ step: 5 }}
      doubleClick={{ mode: "zoomIn", step: 0.6 }}
    >
      {({ zoomIn, zoomOut, resetTransform }) => (
        <Stack spacing={1}>
          <Box
            sx={{
              width: "100%",
              minHeight: 240,
              maxHeight,
              overflow: "hidden",
              borderRadius: 1,
              border: viewerBackground === "#000" ? "1px solid #333" : "1px solid #ddd",
              backgroundColor: viewerBackground,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 1,
            }}
          >
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "100%" }}
              contentStyle={{ width: "100%", height: "100%", display: "flex", justifyContent: "center" }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "inline-block",
                  transform: `rotate(${rotation}deg)`,
                  transformOrigin: "center center",
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt={alt}
                  draggable={false}
                  onLoad={(event) => {
                    const image = event.currentTarget;
                    setNaturalSize({ width: image.naturalWidth || 0, height: image.naturalHeight || 0 });
                  }}
                  sx={{
                    display: "block",
                    maxHeight,
                    width: "auto",
                    maxWidth: "100%",
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                />
                {hasOverlay ? (
                  <Box
                    component="canvas"
                    ref={overlayCanvasRef}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                    }}
                  />
                ) : null}
              </Box>
            </TransformComponent>
          </Box>

          <Stack direction="row" spacing={1} sx={{ my: 1, flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" onClick={() => zoomIn()}>
              Zoom In
            </Button>
            <Button size="small" variant="outlined" onClick={() => zoomOut()}>
              Zoom Out
            </Button>
            <Button size="small" variant="outlined" onClick={() => setRotation((prev) => prev - 90)}>
              Rotate Left
            </Button>
            <Button size="small" variant="outlined" onClick={() => setRotation((prev) => prev + 90)}>
              Rotate Right
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setRotation(0);
                resetTransform();
              }}
            >
              Reset
            </Button>
          </Stack>

          {!hasOverlay ? (
            <Typography variant="body2" color="text.secondary">
              No bounding box data available for this promotion.
            </Typography>
          ) : null}
        </Stack>
      )}
    </TransformWrapper>
  );
}

export default MarketFlyerImageViewer;
